import React, {useEffect} from "react";
import {Theme} from "./Theme";
import {Menu} from "../menu/Menu";
import {MenuTranslationProvider} from "../menu/menuTranslationContext";
import CssBaseline from "@mui/material/CssBaseline";

import {AppProvider} from "./AppContext";
import {WhenHeaderIsReady} from "../header/WhenHeaderIsReady";
import {AppHeader} from "../header/AppHeader";
import {HeaderProvider} from "../header/headerContext";
import {AppContent} from "./AppContent";
import {RoutesAnonymous} from "./routes/RoutesAnonymous";
import {RoutesAuthenticated} from "./routes/RoutesAuthenticated";
import {DmsStatus} from "../dmsstatus/DmsStatus";
import {ProjectProvider} from "../project/projectContext";
import {UpdateAppSnackbar} from "./UpdateAppSnackbar";
import {InstallAppBanner} from "../install-app/InstallAppBanner";
import {FileUploadProvider} from "../damsfileupload/fileUploadContext";
import {DownloadWorkerProvider} from "../workerstatus/workerStatusContext";
import {DMSStatusWorkerListener} from "../workerstatus/dmsStatusWorkerListener";
import {UploadWorkerListener} from "../workerstatus/fileuploads/UploadWorkerListener";

import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {AppOffline} from "./AppOffline";
import {WithUserData} from "../auths/WithUserData";
import {OperationsPopper} from "../operations/OperationsPopper";
import {DocumentProvider} from "../documents/documentContext";
import {SearchProvider} from "../search/SearchContext";
import {DAMSBrowserRouter} from "./DAMSBrowserRouter";
import {clientLog} from "../clientLog";
import {SnackbarProvider} from "notistack";
import {snackbarComponents} from "./snackbarContent";
import {AboutDialog} from "../about/AboutDialog";
import {AboutProvider} from "../about/AboutContext";
import {BrowserLocationProvider} from "../search/document-url/browserLocation";

// QueryClient is stable for the app lifetime — created once outside the component
// so it is never re-instantiated on re-renders.
const queryClient = new QueryClient();

export const App = () => {
    // Side-effect: instantiate Web Workers once after mount.
    useEffect(() => {
        if (typeof window !== "undefined" && typeof Worker !== "undefined") {
            import('./instantiateWorkers.jsx')
                .then(mod => mod.instantiateWorkers())
                .catch(() => {
                    clientLog('warning', 'WebWorkers are not supported in this environment', 'app');
                });
        } else {
            clientLog('warning', 'WebWorkers are not supported in this environment', 'app');
        }
    }, []);

    if (!window._env_ || !window._env_.REACT_APP_API_GATEWAY) {
        return null;
    }

    return (
        <Theme>
            <CssBaseline/>
            <QueryClientProvider client={queryClient}>
                <DAMSBrowserRouter>
                    <AppProvider>
                        <AppOffline/>
                        <HeaderProvider>
                            <AppHeader/>
                            <WhenHeaderIsReady>
                                <UploadWorkerListener/>
                                <DownloadWorkerProvider>
                                    <AppContent>
                                        <RoutesAnonymous/>
                                        {/* WithUserData must be used here, as the menu is operating in the context of the current user */}
                                        <WithUserData>
                                            <SearchProvider>
                                                <MenuTranslationProvider>
                                                    <UpdateAppSnackbar/>
                                                    <InstallAppBanner/>
                                                    <FileUploadProvider>
                                                        <ProjectProvider>
                                                            <AboutProvider>
                                                                <Menu/>
                                                                <AboutDialog/>
                                                            </AboutProvider>
                                                            <DocumentProvider>
                                                                <SnackbarProvider
                                                                    dense={true}
                                                                    maxSnack={3}
                                                                    autoHideDuration={5000}
                                                                    disableWindowBlurListener={true}
                                                                    Components={snackbarComponents}
                                                                    anchorOrigin={{
                                                                        horizontal: "right",
                                                                        vertical: "top"
                                                                    }}>
                                                                    <DMSStatusWorkerListener/>
                                                                    <DmsStatus/>
                                                                    <OperationsPopper/>
                                                                    {/* DE-1720 (ADR-0008): the browser's location, for pages routed for a background */}
                                                                    <BrowserLocationProvider>
                                                                        <RoutesAuthenticated/>
                                                                    </BrowserLocationProvider>
                                                                </SnackbarProvider>
                                                            </DocumentProvider>
                                                        </ProjectProvider>
                                                    </FileUploadProvider>
                                                </MenuTranslationProvider>
                                            </SearchProvider>
                                        </WithUserData>
                                    </AppContent>
                                </DownloadWorkerProvider>
                            </WhenHeaderIsReady>
                        </HeaderProvider>
                    </AppProvider>
                </DAMSBrowserRouter>
            </QueryClientProvider>
        </Theme>
    );
};
